import React, { useState } from 'react';
import { EditIcon, TrashIcon, PlusIcon, SearchIcon, XIcon, MonitorIcon, UsersIcon } from 'lucide-react';
import { Consultant } from '../Consultants';
import { useConsultants } from './ConsultantsContext';
interface ConsultantsListProps {
  onEdit: (consultant: Consultant) => void;
  onDelete: (consultant: Consultant) => void;
  onAdd: () => void;
}
export function ConsultantsList({
  onEdit,
  onDelete,
  onAdd
}: ConsultantsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const {
    consultants,
    loading
  } = useConsultants();
  const filteredConsultants = consultants.filter(consultant => consultant.name.includes(searchQuery) || consultant.title.includes(searchQuery) || consultant.specialization.some(s => s.includes(searchQuery)));
  if (loading) {
    return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-426">
        <div className="flex justify-center items-center h-64" data-id="element-427">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-600 border-t-transparent" data-id="element-428"></div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-429">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4" data-id="element-430">
        <h2 className="text-xl font-bold text-sky-900" data-id="element-431">قائمة الاستشاريين</h2>
        <div className="flex gap-4 w-full md:w-auto" data-id="element-432">
          <div className="relative flex-1 md:w-64" data-id="element-433">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث عن استشاري..." className="w-full px-4 py-2 pr-10 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-434" />
            <SearchIcon className="absolute top-2.5 right-3 h-5 w-5 text-slate-400" data-id="element-435" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute top-2.5 left-3 h-5 w-5 text-slate-400 hover:text-slate-600" data-id="element-436">
                <XIcon className="h-5 w-5" data-id="element-437" />
              </button>}
          </div>
          <button onClick={onAdd} className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center" data-id="element-438">
            <PlusIcon className="h-5 w-5 ml-1.5" data-id="element-439" />
            <span data-id="element-440">إضافة استشاري</span>
          </button>
        </div>
      </div>
      {filteredConsultants.length === 0 ? <div className="text-center py-12 bg-slate-50 rounded-lg" data-id="element-441">
          <p className="text-slate-500 mb-2" data-id="element-442">لا يوجد استشاريين مطابقين لبحثك</p>
          {searchQuery && <button onClick={() => setSearchQuery('')} className="text-sky-600 hover:text-sky-800 font-medium" data-id="element-443">
              عرض جميع الاستشاريين
            </button>}
        </div> : <div className="overflow-x-auto" data-id="element-444">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-445">
            <thead className="bg-slate-50" data-id="element-446">
              <tr data-id="element-447">
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-448">
                  الاستشاري
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-449">
                  التخصصات
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-450">
                  أسعار الاستشارات
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-451">
                  التقييم
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-452">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-453">
              {filteredConsultants.map(consultant => <tr key={consultant.id} className="hover:bg-slate-50" data-id="element-454">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-455">
                    <div className="flex items-center" data-id="element-456">
                      <div className="h-10 w-10 flex-shrink-0" data-id="element-457">
                        <img className="h-10 w-10 rounded-full object-cover" src={consultant.image} alt={consultant.name} data-id="element-458" />
                      </div>
                      <div className="mr-4" data-id="element-459">
                        <div className="text-sm font-medium text-slate-900" data-id="element-460">
                          {consultant.name}
                        </div>
                        <div className="text-sm text-slate-500" data-id="element-461">
                          {consultant.title}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-462">
                    <div className="flex flex-wrap gap-1" data-id="element-463">
                      {consultant.specialization.slice(0, 2).map((spec, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800" data-id="element-464">
                            {spec}
                          </span>)}
                      {consultant.specialization.length > 2 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-465">
                          +{consultant.specialization.length - 2}
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-466">
                    <div className="text-sm text-slate-900 space-y-1" data-id="element-467">
                      {consultant.onlineAvailable && <div className="flex items-center" data-id="element-468">
                          <MonitorIcon className="h-4 w-4 text-green-600 ml-1" data-id="element-469" />
                          <span data-id="element-470">أونلاين: {consultant.price.online}</span>
                        </div>}
                      {consultant.inPersonAvailable && <div className="flex items-center" data-id="element-471">
                          <UsersIcon className="h-4 w-4 text-purple-600 ml-1" data-id="element-472" />
                          <span data-id="element-473">وجاهي: {consultant.price.inPerson}</span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-474">
                    <div className="flex items-center" data-id="element-475">
                      <span className="text-amber-500" data-id="element-476">★</span>
                      <span className="ml-1 text-sm text-slate-900" data-id="element-477">
                        {consultant.rating}
                      </span>
                      <span className="ml-1 text-xs text-slate-500" data-id="element-478">
                        ({consultant.reviewCount})
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium" data-id="element-479">
                    <div className="flex justify-center space-x-2 rtl:space-x-reverse" data-id="element-480">
                      <button onClick={() => onEdit(consultant)} className="text-sky-600 hover:text-sky-900 p-1" title="تعديل" data-id="element-481">
                        <EditIcon className="h-5 w-5" data-id="element-482" />
                      </button>
                      <button onClick={() => onDelete(consultant)} className="text-rose-600 hover:text-rose-900 p-1" title="حذف" data-id="element-483">
                        <TrashIcon className="h-5 w-5" data-id="element-484" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
}